import type { FC, ReactNode } from 'react'
import type { Lakara } from '@/sanskrit/lib/sanskrit/types'

import { useState } from 'react'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { PageHeader, Segmented } from '@/sanskrit/components/vani/bits'
import { useT } from '@/sanskrit/components/providers/app-state'
import { CORE_DHATUS } from '@/sanskrit/lib/sanskrit/dhatus'
import { LAKARA_LABEL, PURUSHA_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import { availableLakaras } from '@/sanskrit/lib/sanskrit/paradigms'
import PurushaGrid from '@/sanskrit/components/grammar/purusha-grid'

const PurushaVachanaPage: FC = (): ReactNode => {
    const t = useT()
    const [rootId, setRootId] = useState('path')
    const [lakara, setLakara] = useState<Lakara>('lat')
    const roots = CORE_DHATUS.filter(d => d.padaClass !== 'atmanepada')
    const lakaras = (['lat', 'lrt', 'lan', 'lot', 'vidhilin'] as Lakara[]).filter(l => availableLakaras(rootId, 'P').includes(l))

    return (
        <Layout>
            <Seo title='Person and number' description='The interactive 3 × 3 grid of Sanskrit person (पुरुष) and number (वचन): pronoun, verb form, explanation and example in every cell.' />
            <PageHeader
                sa='पुरुषवचनम्'
                title={{ hi: 'पुरुष × वचन तालिका', en: 'The person × number grid' }}
                subtitle={{
                    hi: 'तीन पुरुष × तीन वचन = नौ रूप। किसी भी खाने को छुइए।',
                    en: 'Three persons × three numbers = nine forms. Tap any cell.'
                }}
            />
            <div className='mx-auto max-w-5xl space-y-6 px-4 pb-16 sm:px-6'>
                <div className='grid gap-3 sm:grid-cols-3'>
                    {(['uttama', 'madhyama', 'prathama'] as const).map(p => (
                        <div key={p} className='rounded-2xl border border-border bg-card p-4'>
                            <p lang='sa' className='deva text-lg font-semibold text-ink'>{PURUSHA_LABEL[p].sa}</p>
                            <p className='text-sm font-medium'>{t({ hi: PURUSHA_LABEL[p].hi, en: PURUSHA_LABEL[p].en })}</p>
                            <p className='mt-1 text-xs text-muted-foreground'>{t(PURUSHA_LABEL[p].desc)}</p>
                        </div>
                    ))}
                </div>
                <p className='rounded-2xl bg-terracotta-soft/70 px-4 py-3 text-sm'>
                    {t({
                        hi: 'ध्यान दें: संस्कृत का "प्रथम पुरुष" अंग्रेज़ी का third person है, और "उत्तम पुरुष" अंग्रेज़ी का first person।',
                        en: 'Note: Sanskrit "प्रथम पुरुष" is English third person, and "उत्तम पुरुष" is English first person. The order is reversed.'
                    })}
                </p>
                <div className='flex flex-wrap items-center gap-3'>
                    <select value={rootId} onChange={e => setRootId(e.target.value)} className='deva h-10 rounded-xl border border-input bg-card px-3' aria-label={t({ hi: 'धातु', en: 'Root' })}>
                        {roots.map(d => <option key={d.id} value={d.id}>{d.root} ({t(d.meaning)})</option>)}
                    </select>
                    <Segmented size='sm' label={t({ hi: 'लकार', en: 'Lakāra' })} value={lakaras.includes(lakara) ? lakara : 'lat'} onChange={setLakara} options={lakaras.map(l => ({ value: l, label: <span className='deva'>{LAKARA_LABEL[l].sa}</span> }))} />
                </div>
                <div className='rounded-[2rem] border border-border bg-card/60 p-4 sm:p-6'>
                    <PurushaGrid dhatuId={rootId} lakara={lakaras.includes(lakara) ? lakara : 'lat'} />
                </div>
            </div>
        </Layout>
    )
}

export default PurushaVachanaPage
